import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Icon from '../../../../../Icon';
import PrivacyIcon from '../../../../../PrivacyIcon';
import { formatDate } from '../../../../utils';
import { safePhoneNumber } from '../../../utils';
import styles from '../index.styl';

const MedicationCard = ({ entity, formatMessage, isInactive }) => {
  const [activeCard, setActiveCard] = useState(false);

  const {
    name,
    frequency,
    prescribedDate,
    ccdPharmacy,
    providerProfile,
    quantityAmount,
    quantityUnit,
    isPrivate,
    discontinuedDate,
    sources = [],
    memberReportedMedication,
    providerFirstName,
    providerLastName,
  } = entity;

  const safeAddress =
    ccdPharmacy && ccdPharmacy.address1
      ? `${ccdPharmacy.address1 || ccdPharmacy.address2}, ${
          ccdPharmacy.city
        }, ${ccdPharmacy.state}`
      : ccdPharmacy && `${ccdPharmacy.city}, ${ccdPharmacy.state}`;

  const safeName =
    quantityAmount && quantityUnit
      ? `${name}, ${quantityAmount}${quantityUnit}`
      : name;

  const source = sources.join(', ');

  const providerName = useMemo(() => {
    if (providerFirstName && providerLastName) {
      return `${providerFirstName} ${providerLastName}`;
    }

    if (providerProfile) {
      return `${providerProfile.firstName} ${providerProfile.lastName}`;
    }

    return '';
  }, [providerProfile, providerFirstName, providerLastName]);

  return (
    <div className={styles.root}>
      <div className={cn(styles.header, styles.medicationHeader)}>
        <div className={styles.leftSideWrapper}>
          <Icon
            size={28}
            name="icon-medications-black"
            className={cn(styles.iconType, styles.medicationIcon)}
          />
          <div className={styles.contentWrapper}>
            <div className={styles.title}>
              <div
                className={cn(styles.titleWrapper, {
                  [styles.noTitle]: !safeName,
                })}
              >
                {safeName}
                <PrivacyIcon show={isPrivate} className={styles.privacyIcon} />
              </div>
            </div>
            <div
              className={cn(styles.description, styles.medicationDescription)}
            >
              {frequency}
            </div>
            {memberReportedMedication && (
              <div
                className={cn(
                  styles.description,
                  styles.medicationDescription,
                  styles.selfReportedText,
                )}
              >
                {formatMessage({
                  id: 'patient.portal.medications.selfReported',
                })}
              </div>
            )}
          </div>
        </div>
        <div className={cn(styles.rightBlock, styles.medicaationRightBlock)}>
          {isInactive && (
            <span className={styles.inactive}>
              {formatMessage({
                id: 'profile.section.mfaSettings.label.inActive',
              })}
            </span>
          )}
          <Icon
            size={16}
            name={activeCard ? 'icon-arrow-up' : 'icon-arrow-down'}
            className={styles.icon}
            onClick={() => setActiveCard(!activeCard)}
          />
        </div>
      </div>
      {activeCard && (
        <div className={styles.activeCardRoot}>
          <div className={styles.columnWrapper}>
            <div className={styles.column}>
              <div className={styles.meta}>
                {formatMessage({
                  id: memberReportedMedication
                    ? 'patient.portal.medications.startDate'
                    : 'patient.portal.medications.prescribedDate',
                })}
              </div>
              <div className={styles.meta}>
                {formatMessage({
                  id: 'patient.healthboard.source',
                })}
              </div>
              {!memberReportedMedication && (
                <React.Fragment>
                  <div className={styles.meta}>
                    {formatMessage({
                      id: 'patient.portal.medications.pharmacy',
                    })}
                  </div>
                  <div className={styles.meta}>
                    {formatMessage({ id: 'common.phone' })}
                  </div>
                </React.Fragment>
              )}
            </div>
            <div className={styles.column}>
              <div className={styles.context}>
                {formatDate(prescribedDate, 'MMM D, Y')}
              </div>
              <div className={styles.context}>{source || '-'}</div>
              {!memberReportedMedication ? (
                <React.Fragment>
                  <div className={styles.context}>
                    {ccdPharmacy ? ccdPharmacy.name : ''}
                  </div>
                  <div className={styles.context}>
                    {ccdPharmacy ? safePhoneNumber(ccdPharmacy.phone) : ''}
                  </div>
                </React.Fragment>
              ) : null}
            </div>
          </div>
          <div className={styles.columnWrapper}>
            <div className={styles.column}>
              {discontinuedDate && (!memberReportedMedication || isInactive) && (
                <div className={styles.meta}>
                  {formatMessage({
                    id: 'calendar.repeat.endDate',
                  })}
                </div>
              )}
              <div className={styles.meta}>
                {formatMessage({ id: 'profile.actions.provider' })}
              </div>
              {!memberReportedMedication && (
                <div className={styles.meta}>
                  {formatMessage({ id: 'patient.portal.medications.address' })}
                </div>
              )}
            </div>
            <div className={styles.column}>
              {discontinuedDate &&
                (!memberReportedMedication || isInactive) && (
                  <div className={styles.context}>
                    {formatDate(discontinuedDate, 'MMM D, Y')}
                  </div>
                )}
              <div className={styles.context}>
                {providerName}
                {providerProfile && providerProfile.credential
                  ? `, ${providerProfile.credential}`
                  : ''}
              </div>
              {!memberReportedMedication && (
                <div className={cn(styles.context, styles.wrapWhiteSpace)}>
                  {safeAddress || ''}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MedicationCard.propTypes = {
  entity: PropTypes.object,
  isInactive: PropTypes.bool,
  formatMessage: PropTypes.func,
};

export default MedicationCard;



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/DataList/Card/MedicationCard/index.jsx